import React, { useEffect, useRef, useState } from 'react';
import { academicService } from '../services/api';
import SponsoredCourseCard from './SponsoredCourseCard';

/** Self-contained horizontal carousel of admin-sponsored courses. Fetches its
 * own data (public endpoint, works logged-out) so it can drop into both the
 * landing page and the Store with no prop wiring beyond the click handlers. */
export default function SponsoredCoursesRow({ title, subtitle, eyebrow, renderAction, onView, autoAdvance = false, centered = false, onAvailabilityChange }) {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollerRef = useRef(null);

  useEffect(() => {
    academicService.getModules({ sponsored: true })
      .then((res) => {
        const data = res.data;
        const list = Array.isArray(data) ? data : data.results || [];
        setModules(list);
        onAvailabilityChange?.(list.length > 0);
      })
      .catch(() => {
        setModules([]);
        onAvailabilityChange?.(false);
      })
      .finally(() => setLoading(false));
    // Fetch once per mounted carousel; availability callbacks are notifications,
    // not data dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Circular: past the last card wraps to the first, and back past the first
  // wraps to the last — used by both the arrow buttons and auto-advance, so
  // neither ever just stops dead at an edge.
  const scrollByCircular = (delta) => {
    const el = scrollerRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    if (delta > 0 && el.scrollLeft >= maxScroll - 10) {
      el.scrollTo({ left: 0, behavior: 'smooth' });
    } else if (delta < 0 && el.scrollLeft <= 10) {
      el.scrollTo({ left: maxScroll, behavior: 'smooth' });
    } else {
      el.scrollBy({ left: delta, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (!autoAdvance || modules.length < 2) return undefined;
    const id = setInterval(() => scrollByCircular(300), 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoAdvance, modules.length]);

  if (loading || modules.length === 0) return null;

  return (
    <section className="relative">
      {(title || subtitle) && (
        <div className={`mb-9 ${centered ? 'text-center' : ''}`}>
          {eyebrow && <span className="text-xs font-bold uppercase tracking-[0.18em] text-primary-600">{eyebrow}</span>}
          {title && <h2 className={`${eyebrow ? 'mt-2' : ''} text-3xl font-display font-bold tracking-tight text-gray-900`}>{title}</h2>}
          {subtitle && <p className={`text-gray-600 mt-3 ${centered ? 'mx-auto max-w-2xl' : ''}`}>{subtitle}</p>}
          {modules.length > 3 && (
            <div className={`mt-5 hidden gap-2 sm:flex ${centered ? 'justify-center' : ''}`}>
              <button type="button" onClick={() => scrollByCircular(-300)} aria-label="Previous" className="flex h-10 w-10 items-center justify-center rounded-full border border-surface-200 bg-white text-gray-600 shadow-sm transition hover:border-primary-200 hover:text-primary-600">←</button>
              <button type="button" onClick={() => scrollByCircular(300)} aria-label="Next" className="flex h-10 w-10 items-center justify-center rounded-full border border-surface-200 bg-white text-gray-600 shadow-sm transition hover:border-primary-200 hover:text-primary-600">→</button>
            </div>
          )}
        </div>
      )}

      <div className="relative">
        {!centered && modules.length > 2 && (
          <>
            <button
              type="button"
              onClick={() => scrollByCircular(-300)}
              aria-label="Previous"
              className="hidden sm:flex absolute -left-4 top-1/2 -translate-y-1/2 z-10 w-9 h-9 items-center justify-center rounded-full bg-white shadow-card border border-surface-200 hover:bg-surface-50"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
            </button>
            <button
              type="button"
              onClick={() => scrollByCircular(300)}
              aria-label="Next"
              className="hidden sm:flex absolute -right-4 top-1/2 -translate-y-1/2 z-10 w-9 h-9 items-center justify-center rounded-full bg-white shadow-card border border-surface-200 hover:bg-surface-50"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
            </button>
          </>
        )}

        <div
          ref={scrollerRef}
          className={`flex gap-5 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden ${centered && modules.length <= 3 ? 'lg:justify-center' : ''}`}
        >
          {modules.map((m) => (
            <div key={m.id} className={centered ? 'w-[84%] shrink-0 snap-start sm:w-[47%] lg:w-[31.7%]' : 'snap-start'}>
              <SponsoredCourseCard module={m} onView={() => onView?.(m)} action={renderAction?.(m)} centered={centered} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
